import { isPlainObject } from "./shared.js";
import { normalizeSearchTerm } from "./terms.js";

const EMPTY_TERM_CAPABILITIES = Object.freeze({
  canEvaluateEvidenceSample: false,
  canEvaluateFishEntity: false,
  canEvaluateItemEntity: false,
  canEvaluateMapFeature: false,
  canEvaluateUiState: false,
  providesEvidenceSampleScope: false,
});

function termCapabilities(capabilities) {
  return Object.freeze({
    ...EMPTY_TERM_CAPABILITIES,
    ...capabilities,
  });
}

export function searchTermCapabilities(term) {
  const normalized = normalizeSearchTerm(term);
  if (!normalized) {
    return EMPTY_TERM_CAPABILITIES;
  }
  if (normalized.kind === "patch-bound") {
    return termCapabilities({
      canEvaluateEvidenceSample: true,
    });
  }
  if (
    normalized.kind === "fish" ||
    normalized.kind === "status" ||
    normalized.kind === "grade"
  ) {
    return termCapabilities({
      canEvaluateEvidenceSample: true,
      canEvaluateFishEntity: true,
      providesEvidenceSampleScope: true,
    });
  }
  if (
    normalized.kind === "item" ||
    normalized.kind === "item-group" ||
    normalized.kind === "fishing-contest"
  ) {
    return termCapabilities({
      canEvaluateEvidenceSample: true,
      canEvaluateItemEntity: true,
      providesEvidenceSampleScope: true,
    });
  }
  if (normalized.kind === "zone") {
    return termCapabilities({
      canEvaluateEvidenceSample: true,
      canEvaluateMapFeature: true,
      providesEvidenceSampleScope: true,
    });
  }
  if (normalized.kind === "semantic") {
    return termCapabilities({
      canEvaluateMapFeature: true,
    });
  }
  if (normalized.kind === "layer-fact" || normalized.kind === "layer-filter") {
    return termCapabilities({
      canEvaluateMapFeature: true,
    });
  }
  return EMPTY_TERM_CAPABILITIES;
}

export function searchTermIsEvidenceDateConstraint(term) {
  return normalizeSearchTerm(term)?.kind === "patch-bound";
}

export function searchTermProvidesEvidenceSampleScope(term) {
  return searchTermCapabilities(term).providesEvidenceSampleScope;
}

export function searchExpressionNodeHasEvidenceDateConstraint(node) {
  if (!isPlainObject(node)) {
    return false;
  }
  if (node.type === "term") {
    return searchTermIsEvidenceDateConstraint(node.term);
  }
  if (node.type === "container") {
    return searchExpressionNodeHasEvidenceDateConstraint(node.expression);
  }
  return (Array.isArray(node.children) ? node.children : []).some((child) =>
    searchExpressionNodeHasEvidenceDateConstraint(child)
  );
}

export function searchExpressionNodeHasNonDateTerm(node) {
  if (!isPlainObject(node)) {
    return false;
  }
  if (node.type === "term") {
    return !searchTermIsEvidenceDateConstraint(node.term);
  }
  if (node.type === "container") {
    return searchExpressionNodeHasNonDateTerm(node.expression);
  }
  return (Array.isArray(node.children) ? node.children : []).some((child) =>
    searchExpressionNodeHasNonDateTerm(child)
  );
}

export function searchExpressionNodeProvidesEvidenceSampleScope(node) {
  if (!isPlainObject(node)) {
    return false;
  }
  if (node.type === "term") {
    return searchTermProvidesEvidenceSampleScope(node.term);
  }
  if (node.type === "container") {
    return searchExpressionNodeProvidesEvidenceSampleScope(node.expression);
  }
  return (Array.isArray(node.children) ? node.children : []).some((child) =>
    searchExpressionNodeProvidesEvidenceSampleScope(child)
  );
}

export function searchExpressionNodeProvidesEvidenceSampleScopeForEveryBranch(node) {
  if (!isPlainObject(node)) {
    return false;
  }
  if (node.type === "term") {
    return searchTermProvidesEvidenceSampleScope(node.term);
  }
  if (node.type === "container") {
    return searchExpressionNodeProvidesEvidenceSampleScopeForEveryBranch(node.expression);
  }
  const children = Array.isArray(node.children) ? node.children : [];
  if (!children.length) {
    return false;
  }
  const operator = String(node.operator || "or").toLowerCase() === "and" ? "and" : "or";
  if (operator === "and") {
    return children.some((child) =>
      searchExpressionNodeProvidesEvidenceSampleScopeForEveryBranch(child)
    );
  }
  return children.every((child) =>
    searchExpressionNodeProvidesEvidenceSampleScopeForEveryBranch(child)
  );
}

export function collectSearchExpressionDateConstraintNodes(node, out = []) {
  if (!isPlainObject(node)) {
    return out;
  }
  if (node.type === "term") {
    if (searchTermIsEvidenceDateConstraint(node.term)) {
      out.push(node);
    }
    return out;
  }
  if (node.type === "container") {
    collectSearchExpressionDateConstraintNodes(node.expression, out);
    return out;
  }
  for (const child of Array.isArray(node.children) ? node.children : []) {
    collectSearchExpressionDateConstraintNodes(child, out);
  }
  return out;
}
